import { GeneratedBrutalClient } from './generated-rpc';
import type { DrahtClientOptions } from '@geist/drahtzieher-client';
import { RPCError } from '@geist/rpc-core';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface BrutalRpcClientOptions {
  url: string | null;
  onStateChange: ((state: 'connected' | 'disconnected') => void) | null;
}

// ═══════════════════════════════════════════════════════════════
// SINGLETON
// ═══════════════════════════════════════════════════════════════

let client: GeneratedBrutalClient | null = null;

function getWsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

export function getRpcClient(): GeneratedBrutalClient {
  if (client === null) {
    const options: DrahtClientOptions = {
      url: getWsUrl(),
      token: null,
      getToken: null,
      onConnected: null,
      onDisconnected: null,
      onConnectError: null,
      failAfterMs: null,
      onFailed: null,
    };
    client = new GeneratedBrutalClient(options);
    client.connect();
  }
  return client;
}

export function createRpcClient(options: Partial<BrutalRpcClientOptions>): GeneratedBrutalClient {
  const url = options.url ?? getWsUrl();

  if (client === null) {
    const clientOptions: DrahtClientOptions = {
      url,
      token: null,
      getToken: null,
      onConnected: options.onStateChange ? () => options.onStateChange!('connected') : null,
      onDisconnected: options.onStateChange ? () => options.onStateChange!('disconnected') : null,
      onConnectError: null,
      failAfterMs: null,
      onFailed: null,
    };
    client = new GeneratedBrutalClient(clientOptions);
    client.connect();
  }

  return client;
}

/** Legacy-style class wrapper for tests */
export class BrutalRpcClient extends GeneratedBrutalClient {
  constructor(options: Partial<BrutalRpcClientOptions> = {}) {
    const url = options.url ?? 'ws://localhost:3333/ws';
    const clientOptions: DrahtClientOptions = {
      url,
      token: null,
      getToken: null,
      onConnected: options.onStateChange ? () => options.onStateChange!('connected') : null,
      onDisconnected: options.onStateChange ? () => options.onStateChange!('disconnected') : null,
      onConnectError: null,
      failAfterMs: null,
      onFailed: null,
    };
    super(clientOptions);
    this.connect();
  }
}

export type ConnectionState = 'connected' | 'disconnected';
export { RPCError };
